if (!customElements.get('product-modal')) {
  customElements.define(
    'product-modal',
    class ProductModal extends ModalDialog {
      constructor() {
        super();
        this.activePointers = new Map();
        this.zoomScale = 1;
        this.panX = 0;
        this.panY = 0;
        this.gestureStart = null;
        this.pinchStartDistance = 0;
        this.pinchStartScale = 1;
        this.hasPinched = false;
        this.lastTap = { time: 0, x: 0, y: 0 };

        this.addEventListener('pointerup', this.onNavPointerUp.bind(this), { capture: true });
        this.addEventListener('click', this.onNavClick.bind(this));
        this.addEventListener('pointerdown', this.onPointerDown.bind(this));
        this.addEventListener('pointermove', this.onPointerMove.bind(this));
        this.addEventListener('pointerup', this.onPointerUp.bind(this));
        this.addEventListener('pointercancel', this.onPointerCancel.bind(this));
      }

      hide() {
        this.resetZoom();
        this.clearGesture();
        super.hide();
      }

      show(opener) {
        super.show(opener);
        this.showActiveMedia();
      }

      showActiveMedia() {
        this.activateMedia(this.openedBy.getAttribute('data-media-id'));
      }

      activateMedia(mediaId, scrollActiveMedia = true) {
        const visibleItems = this.prioritizeMediaForSelectedVariant();
        const targetMedia =
          visibleItems.find((element) => element.getAttribute('data-media-id') === mediaId) ||
          visibleItems[0] ||
          this.querySelector(`[data-media-id="${mediaId}"]`) ||
          this.mediaItems[0];

        if (!targetMedia) return;

        this.mediaItems.forEach((element) => {
          element.classList.toggle('active', element === targetMedia);
        });

        const activeMedia = this.activeMedia;
        if (!activeMedia) return;

        const activeMediaTemplate = activeMedia.querySelector('template');
        const activeMediaContent = activeMediaTemplate ? activeMediaTemplate.content : null;
        this.resetZoom();
        this.updateNavControls();
        this.updateMediaCounter();

        if (scrollActiveMedia) activeMedia.scrollIntoView({ block: 'nearest', inline: 'center' });

        const container = this.querySelector('[role="document"]');
        if (container && activeMedia.width) {
          container.scrollLeft = (activeMedia.width - container.clientWidth) / 2;
        }

        if (
          activeMedia.nodeName == 'DEFERRED-MEDIA' &&
          activeMediaContent &&
          activeMediaContent.querySelector('.js-youtube')
        )
          activeMedia.loadContent();
      }

      get modalContent() {
        return this.querySelector('.product-media-modal__content');
      }

      get mediaItems() {
        return Array.from(this.querySelectorAll('.product-media-modal__content > [data-media-id]'));
      }

      get visibleMediaItems() {
        return this.mediaItems;
      }

      get activeMedia() {
        return this.querySelector('.product-media-modal__content > .active[data-media-id]');
      }

      get activeImage() {
        return this.activeMedia?.nodeName === 'IMG' ? this.activeMedia : null;
      }

      onNavPointerUp(event) {
        if (event.target.closest('.product-media-modal__nav')) event.stopPropagation();
      }

      onNavClick(event) {
        const button = event.target.closest('.product-media-modal__nav');
        if (!button) return;

        event.preventDefault();
        this.activateAdjacentMedia(Number(button.dataset.mediaDirection || 1));
      }

      onPointerDown(event) {
        if (event.pointerType !== 'touch' || !this.hasAttribute('open')) return;
        if (event.target.closest('.product-media-modal__toggle, .product-media-modal__nav')) return;
        if (!event.target.closest('.product-media-modal__content')) return;

        this.activePointers.set(event.pointerId, {
          startX: event.clientX,
          startY: event.clientY,
          x: event.clientX,
          y: event.clientY,
        });

        this.modalContent?.classList.add('is-gesturing');

        try {
          this.modalContent?.setPointerCapture(event.pointerId);
        } catch (error) {
          // Some browsers skip pointer capture when a gesture begins off-element.
        }

        if (this.activePointers.size === 1) {
          this.gestureStart = {
            panX: this.panX,
            panY: this.panY,
            scale: this.zoomScale,
          };
        }

        if (this.activePointers.size === 2 && this.activeImage) {
          this.pinchStartDistance = this.getPointerDistance();
          this.pinchStartScale = this.zoomScale;
        }

        event.preventDefault();
      }

      onPointerMove(event) {
        const pointer = this.activePointers.get(event.pointerId);
        if (!pointer) return;

        pointer.x = event.clientX;
        pointer.y = event.clientY;

        if (this.activePointers.size >= 2 && this.activeImage && this.pinchStartDistance > 0) {
          const distance = this.getPointerDistance();
          this.zoomScale = this.clamp(this.pinchStartScale * (distance / this.pinchStartDistance), 1, 4);
          this.hasPinched = true;
          this.clampPan();
          this.applyZoom();
          event.preventDefault();
          return;
        }

        if (this.activePointers.size === 1 && this.gestureStart) {
          const deltaX = pointer.x - pointer.startX;
          const deltaY = pointer.y - pointer.startY;

          if (this.zoomScale > 1.02 && this.activeImage) {
            this.panX = this.gestureStart.panX + deltaX;
            this.panY = this.gestureStart.panY + deltaY;
            this.clampPan();
            this.applyZoom();
          }

          event.preventDefault();
        }
      }

      onPointerUp(event) {
        const pointer = this.activePointers.get(event.pointerId);
        if (!pointer) return;

        const wasSinglePointer = this.activePointers.size === 1;
        const deltaX = event.clientX - pointer.startX;
        const deltaY = event.clientY - pointer.startY;
        const movement = Math.hypot(deltaX, deltaY);

        if (wasSinglePointer && !this.hasPinched && this.zoomScale <= 1.02) {
          if (movement < 10 && this.activeImage) {
            this.handleTap(event);
          } else if (Math.abs(deltaX) > 50 && Math.abs(deltaX) > Math.abs(deltaY) * 1.4) {
            this.activateAdjacentMedia(deltaX < 0 ? 1 : -1);
          }
        }

        this.activePointers.delete(event.pointerId);

        if (this.activePointers.size < 2) {
          this.pinchStartDistance = 0;
          this.pinchStartScale = this.zoomScale;
        }

        if (this.activePointers.size === 0) {
          this.modalContent?.classList.remove('is-gesturing');
          this.gestureStart = null;
          this.hasPinched = false;
        }

        try {
          this.modalContent?.releasePointerCapture(event.pointerId);
        } catch (error) {
          // Pointer capture may already be released by the browser.
        }
      }

      onPointerCancel(event) {
        this.activePointers.delete(event.pointerId);
        if (this.activePointers.size === 0) this.clearGesture();
      }

      handleTap(event) {
        const now = Date.now();
        const isDoubleTap =
          now - this.lastTap.time < 320 &&
          Math.hypot(event.clientX - this.lastTap.x, event.clientY - this.lastTap.y) < 28;

        if (isDoubleTap) {
          this.toggleZoom();
          this.lastTap = { time: 0, x: 0, y: 0 };
          return;
        }

        this.lastTap = { time: now, x: event.clientX, y: event.clientY };
      }

      toggleZoom() {
        if (this.zoomScale > 1.02) {
          this.resetZoom();
          return;
        }

        this.zoomScale = 2;
        this.panX = 0;
        this.panY = 0;
        this.applyZoom();
      }

      activateAdjacentMedia(direction) {
        const items = this.visibleMediaItems;
        if (items.length < 2) return;

        const currentIndex = Math.max(
          0,
          items.findIndex((element) => element === this.activeMedia)
        );
        const nextIndex = (currentIndex + direction + items.length) % items.length;
        this.activateMedia(items[nextIndex].getAttribute('data-media-id'), false);
      }

      updateNavControls() {
        const hasMultipleMedia = this.visibleMediaItems.length > 1;
        this.querySelectorAll('.product-media-modal__nav').forEach((button) => {
          button.hidden = !hasMultipleMedia;
          button.disabled = !hasMultipleMedia;
        });
      }

      updateMediaCounter() {
        const counter = this.querySelector('.product-media-modal__counter');
        if (!counter) return;

        const items = this.visibleMediaItems;
        const currentIndex = items.findIndex((element) => element === this.activeMedia);
        const hasMedia = items.length > 0 && currentIndex >= 0;

        counter.hidden = !hasMedia;
        if (hasMedia) counter.textContent = `${currentIndex + 1} / ${items.length}`;
      }

      prioritizeMediaForSelectedVariant() {
        const items = this.mediaItems;
        const terms = this.getSelectedVariantMediaTerms();
        const matches = terms.length
          ? items.filter((element) => this.mediaMatchesTerms(element, terms))
          : [];
        const orderedItems = matches.length
          ? [...matches, ...items.filter((element) => !matches.includes(element))]
          : items;

        this.orderMediaItems(orderedItems);

        this.classList.toggle('is-variant-media-prioritized', matches.length > 0);
        return orderedItems;
      }

      orderMediaItems(items) {
        const container = this.modalContent;
        if (!container || !items.length) return;

        items.forEach((element) => container.appendChild(element));
      }

      getSelectedVariantMediaTerms() {
        const productInfo = document.querySelector(`product-info[data-section="${this.dataset.section}"]`);
        const variantSelects = productInfo?.querySelector('variant-selects');
        const priorityTerms = [];
        const fallbackTerms = [];

        variantSelects?.querySelectorAll('input:checked, select').forEach((control) => {
          const selectedOption = control.matches('select') ? control.selectedOptions?.[0] : control;
          if (!selectedOption) return;

          const optionName =
            selectedOption.dataset.optionName ||
            control.dataset.optionName ||
            control.name ||
            control.closest('fieldset')?.querySelector('legend')?.textContent ||
            '';
          const terms = this.expandVariantTerm(selectedOption.value || selectedOption.textContent || '');
          const targetTerms = /colou?r|shade|tone|style|design/i.test(optionName) ? priorityTerms : fallbackTerms;
          targetTerms.push(...terms);
        });

        if (!priorityTerms.length && !fallbackTerms.length) {
          try {
            const selectedVariant = JSON.parse(variantSelects?.querySelector('[data-selected-variant]')?.textContent);
            selectedVariant?.options?.forEach((option) => fallbackTerms.push(...this.expandVariantTerm(option)));
          } catch {
            // Variant data is optional; no media filtering is safer than an empty modal.
          }
        }

        return [...new Set(priorityTerms.length ? priorityTerms : fallbackTerms)];
      }

      expandVariantTerm(value) {
        const normalized = this.normalizeSearchText(value);
        if (!normalized) return [];

        const terms = [normalized];
        normalized
          .split(' ')
          .filter((term) => term.length >= 3)
          .forEach((term) => terms.push(term));

        return terms.filter((term) => term.length >= 3);
      }

      mediaMatchesTerms(element, terms) {
        const mediaText = ` ${this.normalizeSearchText(element.dataset.mediaFilterText || '')} `;
        return terms.some((term) => mediaText.includes(` ${term} `));
      }

      normalizeSearchText(value) {
        return String(value || '')
          .toLowerCase()
          .replace(/&amp;/g, ' and ')
          .replace(/[^a-z0-9]+/g, ' ')
          .replace(/\s+/g, ' ')
          .trim();
      }

      resetZoom() {
        this.zoomScale = 1;
        this.panX = 0;
        this.panY = 0;

        this.querySelectorAll('.product-media-modal__content > img').forEach((image) => {
          image.style.setProperty('--tw-media-scale', 1);
          image.style.setProperty('--tw-media-pan-x', '0px');
          image.style.setProperty('--tw-media-pan-y', '0px');
        });

        this.modalContent?.classList.remove('is-zoomed');
      }

      applyZoom() {
        const image = this.activeImage;
        if (!image) return;

        image.style.setProperty('--tw-media-scale', this.zoomScale.toFixed(3));
        image.style.setProperty('--tw-media-pan-x', `${this.panX}px`);
        image.style.setProperty('--tw-media-pan-y', `${this.panY}px`);
        this.modalContent?.classList.toggle('is-zoomed', this.zoomScale > 1.02);
      }

      clampPan() {
        const image = this.activeImage;
        const container = this.modalContent;
        if (!image || !container || this.zoomScale <= 1) {
          this.panX = 0;
          this.panY = 0;
          return;
        }

        const maxPanX = Math.max(0, (image.offsetWidth * this.zoomScale - container.clientWidth) / 2);
        const maxPanY = Math.max(0, (image.offsetHeight * this.zoomScale - container.clientHeight) / 2);
        this.panX = this.clamp(this.panX, -maxPanX, maxPanX);
        this.panY = this.clamp(this.panY, -maxPanY, maxPanY);
      }

      getPointerDistance() {
        const pointers = Array.from(this.activePointers.values());
        if (pointers.length < 2) return 0;

        return Math.hypot(pointers[0].x - pointers[1].x, pointers[0].y - pointers[1].y);
      }

      clearGesture() {
        this.activePointers.clear();
        this.gestureStart = null;
        this.pinchStartDistance = 0;
        this.hasPinched = false;
        this.modalContent?.classList.remove('is-gesturing');
      }

      clamp(value, min, max) {
        return Math.min(Math.max(value, min), max);
      }
    }
  );
}
